import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useState, useEffect, useRef } from "react";
import gamingSetup from "@/assets/gaming-setup.jpg";

const COUNTDOWN_FROM = 48 * 60 * 60; // 48 hours in seconds

const HeroSection = () => {
  const [timeLeft, setTimeLeft] = useState(COUNTDOWN_FROM);
  const startRef = useRef(Date.now());

  useEffect(() => {
    const id = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startRef.current) / 1000);
      const remaining = Math.max(0, COUNTDOWN_FROM - elapsed);
      setTimeLeft(remaining);
      if (remaining <= 0) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const hours = String(Math.floor(timeLeft / 3600)).padStart(2, "0");
  const mins = String(Math.floor((timeLeft % 3600) / 60)).padStart(2, "0");
  const secs = String(timeLeft % 60).padStart(2, "0");

  const marqueeText =
    "🎮 FREE SHIPPING ABOVE ₹999  •  ⚡ SEASON 2 DROP LIVE  •  💜 50,000+ GAMERS TRUST GROVEFAN  •  🔥 NEW DROPS EVERY FRIDAY  •  ";

  return (
    <>
      {/* Hero */}
      <section
        className="relative w-full min-h-screen overflow-hidden"
        style={{ background: "#0d0b1a" }}
      >
        {/* Radial glow */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 60% 50% at 70% 40%, rgba(83,74,183,0.18) 0%, transparent 70%)",
          }}
        />

        {/* Grid overlay */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(127,119,221,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(127,119,221,0.4) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />

        <div className="relative z-10 mx-auto flex min-h-screen max-w-7xl items-center px-5 py-20 lg:px-8">
          <div className="grid w-full gap-12 lg:grid-cols-2 lg:gap-16 items-center">
            {/* LEFT COLUMN */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              {/* Pill badge */}
              <span
                className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-semibold tracking-wide"
                style={{ background: "#26215C", color: "#AFA9EC" }}
              >
                ⚡ SEASON 2 DROP IS LIVE
              </span>

              {/* Headline */}
              <h1 className="mt-6 font-display text-[3.2rem] font-black leading-[1.08] sm:text-[3.8rem] lg:text-[4rem]">
                <span style={{ color: "#EEEDFE" }}>Gear Up.</span>
                <br />
                <span style={{ color: "#7F77DD" }}>Game On.</span>
              </h1>

              {/* Sub */}
              <p
                className="mt-5 max-w-md text-base leading-relaxed"
                style={{ color: "#AFA9EC" }}
              >
                India's first gaming marketplace for the next generation.
                Premium gear, exclusive drops, and a community that plays as
                hard as it dresses.
              </p>

              {/* CTAs */}
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/shop"
                  className="inline-flex items-center justify-center gap-2 rounded-lg px-7 py-3.5 text-sm font-bold transition-all hover:brightness-110"
                  style={{
                    background: "#534AB7",
                    color: "#fff",
                    boxShadow: "0 0 24px rgba(83,74,183,0.35)",
                  }}
                >
                  Shop All Gear
                </Link>
                <Link
                  to="/shop"
                  className="inline-flex items-center justify-center gap-2 rounded-lg border px-7 py-3.5 text-sm font-bold transition-all hover:bg-[#26215C]/40"
                  style={{
                    borderColor: "#7F77DD",
                    color: "#7F77DD",
                    background: "transparent",
                  }}
                >
                  View Drops ↓
                </Link>
              </div>

              {/* Trust stats */}
              <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs" style={{ color: "#AFA9EC" }}>
                <span>🚚 Free shipping above ₹999</span>
                <span>⭐ 4.9 rated by 50k+ gamers</span>
                <span>🔒 Secure checkout</span>
              </div>
            </motion.div>

            {/* RIGHT COLUMN */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="hidden lg:flex flex-col items-center gap-6"
            >
              {/* Gaming setup card */}
              <div
                className="w-full overflow-hidden rounded-2xl border"
                style={{
                  background: "#1a1730",
                  borderColor: "#3C3489",
                  boxShadow: "0 0 60px rgba(83,74,183,0.15)",
                }}
              >
                <img
                  src={gamingSetup}
                  alt="Gaming setup with purple-lit monitor, keyboard and headset"
                  className="w-full object-cover"
                  width={768}
                  height={640}
                />
              </div>

              {/* Countdown */}
              <div className="flex flex-col items-center gap-2">
                <span
                  className="text-xs font-semibold uppercase tracking-widest"
                  style={{ color: "#AFA9EC" }}
                >
                  Next Drop In
                </span>
                <div className="flex items-center gap-2">
                  {[
                    { val: hours, label: "HOURS" },
                    { val: mins, label: "MINS" },
                    { val: secs, label: "SECS" },
                  ].map((t, i) => (
                    <div key={t.label} className="flex items-center gap-2">
                      <div className="flex flex-col items-center">
                        <span
                          className="flex h-14 w-16 items-center justify-center rounded-lg border font-display text-2xl font-black"
                          style={{
                            background: "#1a1730",
                            borderColor: "#3C3489",
                            color: "#EEEDFE",
                          }}
                        >
                          {t.val}
                        </span>
                        <span
                          className="mt-1 text-[10px] font-semibold tracking-widest"
                          style={{ color: "#7F77DD" }}
                        >
                          {t.label}
                        </span>
                      </div>
                      {i < 2 && (
                        <span
                          className="mb-4 text-xl font-bold"
                          style={{ color: "#3C3489" }}
                        >
                          :
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* MOBILE countdown (visible on small screens) */}
            <div className="flex flex-col items-center gap-2 lg:hidden">
              <span
                className="text-xs font-semibold uppercase tracking-widest"
                style={{ color: "#AFA9EC" }}
              >
                Next Drop In
              </span>
              <div className="flex items-center gap-2">
                {[
                  { val: hours, label: "HOURS" },
                  { val: mins, label: "MINS" },
                  { val: secs, label: "SECS" },
                ].map((t, i) => (
                  <div key={t.label} className="flex items-center gap-2">
                    <div className="flex flex-col items-center">
                      <span
                        className="flex h-12 w-14 items-center justify-center rounded-lg border font-display text-xl font-black"
                        style={{
                          background: "#1a1730",
                          borderColor: "#3C3489",
                          color: "#EEEDFE",
                        }}
                      >
                        {t.val}
                      </span>
                      <span
                        className="mt-1 text-[10px] font-semibold tracking-widest"
                        style={{ color: "#7F77DD" }}
                      >
                        {t.label}
                      </span>
                    </div>
                    {i < 2 && (
                      <span
                        className="mb-4 text-lg font-bold"
                        style={{ color: "#3C3489" }}
                      >
                        :
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee ticker */}
      <div
        className="relative w-full overflow-hidden py-3"
        style={{ background: "#0d0b1a", borderTop: "1px solid #3C3489", borderBottom: "1px solid #3C3489" }}
      >
        <div className="marquee-track flex whitespace-nowrap">
          <span
            className="inline-block min-w-max px-4 text-sm font-semibold tracking-wide"
            style={{ color: "#7F77DD" }}
          >
            {marqueeText}{marqueeText}
          </span>
          <span
            className="inline-block min-w-max px-4 text-sm font-semibold tracking-wide"
            style={{ color: "#7F77DD" }}
          >
            {marqueeText}{marqueeText}
          </span>
        </div>
      </div>
    </>
  );
};

export default HeroSection;
